// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Address, Extension, Identifier, Patient } from '@medplum/fhirtypes';

// ─── Identifier system ─────────────────────────────────────────────────────

export const STRIPE_CUSTOMER_IDENTIFIER_SYSTEM = 'https://stripe.com/customer';
export const STRIPE_PAYMENT_INTENT_IDENTIFIER_SYSTEM = 'https://stripe.com/payment-intent-id';

// ─── Extension URLs ────────────────────────────────────────────────────────

export const PATIENT_PAYMENT_METHOD_EXTENSION_URL =
  'https://polarissleep.com/fhir/StructureDefinition/patient-payment-method';

export const PAYMENT_METHOD_ID_URL = `${PATIENT_PAYMENT_METHOD_EXTENSION_URL}/paymentMethodId`;
export const PAYMENT_METHOD_BRAND_URL = `${PATIENT_PAYMENT_METHOD_EXTENSION_URL}/brand`;
export const PAYMENT_METHOD_LAST4_URL = `${PATIENT_PAYMENT_METHOD_EXTENSION_URL}/last4`;
export const PAYMENT_METHOD_EXP_MONTH_URL = `${PATIENT_PAYMENT_METHOD_EXTENSION_URL}/expMonth`;
export const PAYMENT_METHOD_EXP_YEAR_URL = `${PATIENT_PAYMENT_METHOD_EXTENSION_URL}/expYear`;
export const PAYMENT_METHOD_CARDHOLDER_NAME_URL = `${PATIENT_PAYMENT_METHOD_EXTENSION_URL}/cardholderName`;
export const PAYMENT_METHOD_BILLING_ADDRESS_URL = `${PATIENT_PAYMENT_METHOD_EXTENSION_URL}/billingAddress`;

// ─── Type ──────────────────────────────────────────────────────────────────

export interface PaymentMethodDisplay {
  paymentMethodId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  cardholderName: string;
  billingAddress?: Address;
}

// ─── Stripe customer identifier helpers ────────────────────────────────────

export function getStripeCustomerId(patient: Patient): string | undefined {
  return patient.identifier?.find((i) => i.system === STRIPE_CUSTOMER_IDENTIFIER_SYSTEM)?.value;
}

export function setStripeCustomerId(patient: Patient, customerId: string): Patient {
  const others: Identifier[] = (patient.identifier ?? []).filter((i) => i.system !== STRIPE_CUSTOMER_IDENTIFIER_SYSTEM);
  return {
    ...patient,
    identifier: [...others, { system: STRIPE_CUSTOMER_IDENTIFIER_SYSTEM, value: customerId }],
  };
}

// ─── Payment method extension helpers ──────────────────────────────────────

function buildPaymentMethodExtension(display: PaymentMethodDisplay): Extension {
  const sub: Extension[] = [
    { url: PAYMENT_METHOD_ID_URL, valueString: display.paymentMethodId },
    { url: PAYMENT_METHOD_BRAND_URL, valueString: display.brand },
    { url: PAYMENT_METHOD_LAST4_URL, valueString: display.last4 },
    { url: PAYMENT_METHOD_EXP_MONTH_URL, valueInteger: display.expMonth },
    { url: PAYMENT_METHOD_EXP_YEAR_URL, valueInteger: display.expYear },
    { url: PAYMENT_METHOD_CARDHOLDER_NAME_URL, valueString: display.cardholderName },
  ];
  if (display.billingAddress) {
    sub.push({ url: PAYMENT_METHOD_BILLING_ADDRESS_URL, valueAddress: display.billingAddress });
  }
  return { url: PATIENT_PAYMENT_METHOD_EXTENSION_URL, extension: sub };
}

export function getPatientPaymentMethod(patient: Patient): PaymentMethodDisplay | undefined {
  const ext = patient.extension?.find((e) => e.url === PATIENT_PAYMENT_METHOD_EXTENSION_URL);
  if (!ext?.extension) {
    return undefined;
  }
  const findString = (url: string): string | undefined => ext.extension?.find((e) => e.url === url)?.valueString;
  const findInteger = (url: string): number | undefined => ext.extension?.find((e) => e.url === url)?.valueInteger;

  const paymentMethodId = findString(PAYMENT_METHOD_ID_URL);
  const brand = findString(PAYMENT_METHOD_BRAND_URL);
  const last4 = findString(PAYMENT_METHOD_LAST4_URL);
  const expMonth = findInteger(PAYMENT_METHOD_EXP_MONTH_URL);
  const expYear = findInteger(PAYMENT_METHOD_EXP_YEAR_URL);
  const cardholderName = findString(PAYMENT_METHOD_CARDHOLDER_NAME_URL);
  if (!paymentMethodId || !brand || !last4 || expMonth == null || expYear == null || cardholderName == null) {
    return undefined;
  }
  const billingAddress = ext.extension.find((e) => e.url === PAYMENT_METHOD_BILLING_ADDRESS_URL)?.valueAddress;
  return { paymentMethodId, brand, last4, expMonth, expYear, cardholderName, billingAddress };
}

export function setPatientPaymentMethod(patient: Patient, display: PaymentMethodDisplay): Patient {
  const others = (patient.extension ?? []).filter((e) => e.url !== PATIENT_PAYMENT_METHOD_EXTENSION_URL);
  return { ...patient, extension: [...others, buildPaymentMethodExtension(display)] };
}

export function clearPatientPaymentMethod(patient: Patient): Patient {
  if (!patient.extension?.some((e) => e.url === PATIENT_PAYMENT_METHOD_EXTENSION_URL)) {
    return patient;
  }
  return {
    ...patient,
    extension: patient.extension.filter((e) => e.url !== PATIENT_PAYMENT_METHOD_EXTENSION_URL),
  };
}
